import { useDeferredValue } from 'react';
import { useReadContract } from 'wagmi';

import { PancakePoolManager } from '@/shared/abi';

import { ENetwork, web3Configs } from 'web3';

import { CONTRACTS } from './contracts';
import { PancakeInfiniteExtra, PoolKey } from './types';

type UsePancakePoolDataParams = {
  poolId: string;
  enabled?: boolean;
};

type UsePancakePoolDataReturn = {
  poolKey: PoolKey | undefined;
  extra: PancakeInfiniteExtra | undefined;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Type for wagmi poolIdToPoolKey response
 * Can be either array or object depending on wagmi version
 */
type PoolKeyResponse =
  | readonly [string, string, string, string, number, string]
  | {
      currency0: string;
      currency1: string;
      hooks: string;
      poolManager: string;
      fee: number;
      parameters: string;
    };

/**
 * Hook for fetching PancakeSwap Infinite CL pool data by pool ID
 *
 * @param poolId - The pool ID (bytes32 hex string)
 * @param enabled - Whether to enable the query
 *
 * @returns Pool key data, extra data for API, loading state, and error
 *
 * @example
 * ```tsx
 * const { poolKey, extra, isLoading } = usePancakePoolData({
 *   poolId: '0xd37aa0f0d66ad670279f6b89325c88bdff17d0265144762fb01f54fca9779944',
 * });
 * ```
 */
export function usePancakePoolData({
  poolId,
  enabled = true,
}: UsePancakePoolDataParams): UsePancakePoolDataReturn {
  const deferredPoolId = useDeferredValue(poolId);

  const isValidPoolId = /^0x[0-9a-fA-F]{64}$/.test(deferredPoolId);

  const {
    data: poolKeyData,
    isLoading,
    error,
  } = useReadContract({
    abi: PancakePoolManager,
    address: CONTRACTS[ENetwork.bsc].PANCAKE_POOL_MANAGER,
    chainId: web3Configs[ENetwork.bsc].chainId,
    functionName: 'poolIdToPoolKey',
    args: [deferredPoolId as `0x${string}`],
    query: {
      enabled: enabled && isValidPoolId && deferredPoolId.length === 66,
    },
  });

  if (!poolKeyData) {
    return {
      poolKey: undefined,
      extra: undefined,
      isLoading,
      error: error as Error | null,
    };
  }

  let currency0: string;
  let currency1: string;
  let hooks: string;
  let poolManager: string;
  let fee: number;
  let parameters: string;

  if (Array.isArray(poolKeyData)) {
    [currency0, currency1, hooks, poolManager, fee, parameters] = poolKeyData;
  } else if (typeof poolKeyData === 'object') {
    // Handle object return type (if wagmi returns named outputs)
    const data = poolKeyData as PoolKeyResponse & Record<string, unknown>;
    currency0 = data.currency0 as string;
    currency1 = data.currency1 as string;
    hooks = data.hooks as string;
    poolManager = data.poolManager as string;
    fee = Number(data.fee);
    parameters = data.parameters as string;
  } else {
    return {
      poolKey: undefined,
      extra: undefined,
      isLoading,
      error: new Error('Unexpected contract return type'),
    };
  }

  // Validate pool data
  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
  const isZeroAddress = (address: string) => address.toLowerCase() === ZERO_ADDRESS.toLowerCase();

  if (isZeroAddress(currency0) || isZeroAddress(currency1)) {
    return {
      poolKey: undefined,
      extra: undefined,
      isLoading,
      error: new Error('Invalid pool: one or both currencies are zero address'),
    };
  }

  if (currency0.toLowerCase() === currency1.toLowerCase()) {
    return {
      poolKey: undefined,
      extra: undefined,
      isLoading,
      error: new Error('Invalid pool: currency0 and currency1 are the same'),
    };
  }

  const poolKey: PoolKey = {
    currency0,
    currency1,
    hooks,
    poolManager,
    fee,
    parameters,
  };

  const extra: PancakeInfiniteExtra = {
    currency0: poolKey.currency0,
    currency1: poolKey.currency1,
    hooks: poolKey.hooks,
    pool_manager: poolKey.poolManager,
    fee: poolKey.fee,
    parameters: poolKey.parameters,
  };

  return {
    poolKey,
    extra,
    isLoading,
    error: error as Error | null,
  };
}
